import {
  applyCatchupCompleteFrame,
  applyCatchupPageFrame,
  applyLiveSyncEvent,
  applySyncReadyFrame,
  buildHandshakeFrame,
  evaluateSyncReadyContinuity,
  resetResumeForDisconnect,
  type ThreadSyncResumeState,
} from "@/lib/thread-sync-resume-state"
import {
  buildSyncReadyDebugSnapshot,
  type SyncReadyDebugSnapshot,
} from "@/lib/thread-sync-history-token-debug"
import type { ThreadRuntimeMaterialization } from "@/lib/thread-sync-runtime-materialization"

const NON_RECOVERABLE_SYNC_ERROR_CODES = new Set([
  "invalid_message",
  "invalid_json",
  "invalid_frame",
  "unsupported_frame",
])

export function planSyncHeartbeat(args: {
  helloSent: boolean
  socketReady: boolean
  cursor: number
  viewerActivityEnabled: boolean
}):
  | {
      shouldSend: false
    }
  | {
      shouldSend: true
      requestedAfter: number
      payload: ReturnType<typeof buildHandshakeFrame>["payload"]
    } {
  if (args.helloSent || !args.socketReady) {
    return { shouldSend: false }
  }

  const handshake = buildHandshakeFrame({
    cursor: args.cursor,
    viewerActivityEnabled: args.viewerActivityEnabled,
  })

  return {
    shouldSend: true,
    requestedAfter: handshake.requestedAfter,
    payload: handshake.payload,
  }
}

export function applyBaselinePageMetrics(args: {
  resume: ThreadSyncResumeState
  frame: { count: number; approxBytes: number }
}): ThreadSyncResumeState {
  return {
    ...args.resume,
    lastPageCount: args.frame.count,
    lastPageBytes: args.frame.approxBytes,
  }
}

export function planSyncReadyHandling(args: {
  resume: ThreadSyncResumeState
  frame: {
    acceptedCursor: number
    tailId: number
    historyToken: string | null
    historyContinuity: "contiguous" | "broken"
    replicationContinuity: "contiguous" | "broken"
  }
  runtimeMaterialization: ThreadRuntimeMaterialization
  runtimeConnectionVersion: number | null
  transportConnectionVersion: number
  hasActiveRuntimeState: boolean
  hasActiveRuntimeSnapshot: boolean
  activeLastEventId: number | null
  nowMs: number
}): {
  nextResume: ThreadSyncResumeState
  continuityValidatedConnectionVersion: number
  shouldResetRuntime: boolean
  shouldReplaceDisplayOnlyRuntime: boolean
  debugSnapshot: SyncReadyDebugSnapshot
} {
  const previousResume = args.resume
  const nextResume = applySyncReadyFrame(args.resume, args.frame)
  const continuityDecision = evaluateSyncReadyContinuity({
    historyContinuity: args.frame.historyContinuity,
    previousHistoryToken: previousResume.historyToken,
    nextHistoryToken: args.frame.historyToken,
    acceptedCursor: args.frame.acceptedCursor,
    runtimeMaterialization: args.runtimeMaterialization,
    runtimeConnectionVersion: args.runtimeConnectionVersion,
    connectionVersion: args.transportConnectionVersion,
    activeLastEventId: args.activeLastEventId,
    hasActiveRuntimeSnapshot: args.hasActiveRuntimeSnapshot,
  })
  const shouldResetRuntime = args.hasActiveRuntimeState && continuityDecision.shouldResetRuntime

  return {
    nextResume,
    continuityValidatedConnectionVersion: args.transportConnectionVersion,
    shouldResetRuntime,
    shouldReplaceDisplayOnlyRuntime: nextResume.isCatchingUp === false,
    debugSnapshot: buildSyncReadyDebugSnapshot({
      nowMs: args.nowMs,
      resetTriggered: shouldResetRuntime,
      historyContinuity: args.frame.historyContinuity,
      replicationContinuity: args.frame.replicationContinuity,
      hasHistoryContinuityBroken: continuityDecision.hasHistoryContinuityBroken,
      hasHistoryTokenMismatch: continuityDecision.hasHistoryTokenMismatch,
      hasCursorRegression: continuityDecision.hasCursorRegression,
      hasActiveRuntimeSnapshot: args.hasActiveRuntimeSnapshot,
      hasCurrentConnectionAuthoritativeRuntime:
        continuityDecision.hasCurrentConnectionAuthoritativeRuntime,
      acceptedCursor: args.frame.acceptedCursor,
      tailCursor: args.frame.tailId,
      activeLastEventId: args.activeLastEventId,
      runtimeMaterialization: args.runtimeMaterialization,
      previousHistoryToken: previousResume.historyToken,
      nextHistoryToken: args.frame.historyToken,
    }),
  }
}

export function shouldCaptureAuthoritativeRuntime(args: {
  activeThreadId: string | null
  sessionId: string
  runtimeMaterialization: ThreadRuntimeMaterialization
  isCatchingUp: boolean
}): boolean {
  return (
    args.activeThreadId === args.sessionId &&
    args.isCatchingUp === false &&
    (args.runtimeMaterialization === "authoritative_partial" ||
      args.runtimeMaterialization === "authoritative")
  )
}

export function planCatchupPageHandling(args: {
  resume: ThreadSyncResumeState
  frame: {
    count: number
    approxBytes: number
    nextCursor: number
  }
  applyResultStatus: "applied" | "noop" | "error"
}): ThreadSyncResumeState {
  return applyCatchupPageFrame(args.resume, args.frame, {
    appliedWithError: args.applyResultStatus === "error",
  })
}

export function planCatchupCompleteHandling(args: {
  resume: ThreadSyncResumeState
  frame: { finalCursor: number }
}): ThreadSyncResumeState {
  return applyCatchupCompleteFrame(args.resume, args.frame)
}

export function planLiveSyncEventHandling(args: {
  resume: ThreadSyncResumeState
  eventId: number
  applyResultStatus: "applied" | "noop" | "error"
}): ThreadSyncResumeState {
  return applyLiveSyncEvent(args.resume, {
    eventId: args.eventId,
    appliedWithError: args.applyResultStatus === "error",
  })
}

export function planSyncErrorHandling(args: {
  resume: ThreadSyncResumeState
  frame: { code: string; message: string }
}): {
  nextResume: ThreadSyncResumeState
  reason: string
  shouldReconnect: boolean
  closeReason: string
} {
  return {
    nextResume: resetResumeForDisconnect(args.resume),
    reason: buildSyncErrorReason(args.frame),
    shouldReconnect: NON_RECOVERABLE_SYNC_ERROR_CODES.has(args.frame.code) === false,
    closeReason: `sync_error:${args.frame.code}`,
  }
}

export function buildSyncErrorReason(frame: { code: string; message: string }): string {
  return `${frame.code}: ${frame.message}`
}
